import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { guslStorage } from 'src/providers/session/GuslStorage';
import { VISIONX_CONSTANTS } from 'src/app/ComponentMap.tsx';
import { isDefined } from 'src/utils/TypeCheckers.ts';
import { useNavigate } from 'react-router-dom';
import { environmentService } from 'src/providers/environment/EnvironmentService';
import { AuthType } from 'src/providers/environment/types';

interface AxiosClientConfig extends AxiosRequestConfig {
  contentType?: string;
}

const commonConfig: AxiosRequestConfig = {
  timeout: 60000,
  headers: {
    accept: 'application/json',
  },
};

export interface CreateAxiosClientsDO {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  renewSession: Function;
}

const EXCLUDE_HEADERS_FOR: string[] = [
  '/totp/v1/qrcode',
  '/security/login',
  '/user/verify',
  '/security/v1/sign-in',
  '/security/v1/pre-sign-in',
  'ui-props',
  '/security/v1/token-login',
  'user/forgot/password-confirmation',
  '/user/change-password/qrcode',
  '/user/qrcode-validate',
];
const TOTP_USER_TOKEN_HEADER = 'totp-user-token';
const TOTP_SESSION_TOKEN_HEADER = 'totp-session-token';

const PRE_SESSION_ENDPOINTS: string[] = [
  '/security/v1/pre-sign-in',
  '/security/v1/sign-in',
  '/user/change-password/qrcode',
  '/user/qrcode-validate',
];

export function CreateAxiosClients({ renewSession }: CreateAxiosClientsDO) {
  const addHeaders = (config: AxiosRequestConfig): AxiosRequestConfig => {
    if (guslStorage.isJwt()) {
      const jwtSession = guslStorage.getJwtSession();
      if (jwtSession) {
        if (config.headers) {
          if (
            environmentService.getEnvironment()?.authType === AuthType.TIME_BASED_ONE_TIME_PASSWORD
          ) {
            config.headers[TOTP_SESSION_TOKEN_HEADER] = jwtSession.sessionToken || '';
            config.headers[TOTP_USER_TOKEN_HEADER] = jwtSession.userToken || '';
          } else {
            config.headers['Authorization'] = 'Bearer ' + jwtSession.accessToken || '';
          }
        }
      }
    }

    return config;
  };
  const navigate = useNavigate();
  const createAxiosInstance = (config: AxiosClientConfig): AxiosInstance => {
    const axiosInstance = axios.create({
      ...commonConfig,
      ...config,
      headers: {
        ...commonConfig.headers,
        ...config.headers,
      },
    });

    axiosInstance.interceptors.request.use(
      (requestConfig) => {
        const excludeHeaders = (config: AxiosRequestConfig) => {
          return isDefined(EXCLUDE_HEADERS_FOR.find((hdr) => config.url?.endsWith(hdr)));
        };

        if (excludeHeaders(requestConfig)) {
          return requestConfig;
        }
        return addHeaders(requestConfig) as typeof requestConfig;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    axiosInstance.interceptors.request.use(
      (config) => {
        const tenantId = sessionStorage.getItem('tenantId');
        if (tenantId) {
          if (!config.params) {
            config.params = {};
          }
          config.params['tenantId'] = tenantId;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        return Promise.resolve(response);
      },
      (error) => {
        const config = error.config;

        const handleAuthError = () => {
          if (PRE_SESSION_ENDPOINTS.some((url) => config.url?.endsWith(url))) {
            return Promise.reject(error);
          }
          if (config._retried || config.url?.endsWith('token-login')) {
            if (config.url?.endsWith('token-login')) {
              navigate(VISIONX_CONSTANTS.LOGIN_PATH);
              guslStorage.clearSession();
            }
            return Promise.reject(error);
          }

          config._retried = true;

          return renewSession()
            .then(() => {
              return axios(addHeaders(config));
            })
            .catch(() => {
              return Promise.reject(error);
            });
        };

        const handleErrorByStatus = () => {
          switch (error.response?.status) {
            case 401:
              return handleAuthError();
            case 402:
              navigate(VISIONX_CONSTANTS.LOGIN_PATH);
              return Promise.reject(error);
            case 406:
              navigate(VISIONX_CONSTANTS.GEO_RESTRICTED_PATH);
              return Promise.reject(error);
            default:
              return Promise.reject(error.response);
          }
        };

        return handleErrorByStatus();
      },
    );

    return axiosInstance;
  };

  const axiosClient = createAxiosInstance({
    method: 'post',
    contentType: 'application/json',
  });
  const axiosClientUpload = createAxiosInstance({
    method: 'post',
    contentType: 'multipart/form-data',
  });
  const axiosClientSignInSignOut = createAxiosInstance({
    method: 'post',
    contentType: 'application/x-www-form-urlencoded',
  });

  return { axiosClient, axiosClientSignInSignOut, axiosClientUpload };
}
